import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import Toast from '../global/Toast';
import { BRANDING } from '../config';
import { STATIONS } from './AccountabilityHome';
import '../global/styles.css';

const MEDICATIONS = [
  { id: 'morphine_sulfate', label: 'Morphine Sulfate' },
  { id: 'fentanyl', label: 'Fentanyl' },
  { id: 'ketamine', label: 'Ketamine' },
  { id: 'ketamine_500mg', label: 'Ketamine 500mg/5mL' },
  { id: 'midazolam', label: 'Midazolam 5mg/mL' }
];

const ROUTES = [
  { id: 'IN', label: 'IN' },
  { id: 'SQ', label: 'SQ' },
  { id: 'IV', label: 'IV' },
  { id: 'IO', label: 'IO' },
  { id: 'IM', label: 'IM' }
];

function AccountabilityForm({ stationId, userInfo, isAdmin, onLogout, onNavigate }) {
  const station = STATIONS.find(s => s.id === stationId) || STATIONS[0];

  const [formData, setFormData] = useState({
    formDate: new Date().toISOString().split('T')[0],
    patientName: '',
    incidentNumber: '',
    medication: '',
    totalAmountAdministered: '',
    routeAdministered: '',
    paramedicSignature: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // Canvas ref
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Signature canvas methods
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    canvas.width = Math.min(window.innerWidth * 0.8, 500);
    canvas.height = window.innerHeight * 0.35;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineWidth = 2;
    context.strokeStyle = '#000000';
    context.lineJoin = 'round';
    context.lineCap = 'round';
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    let x, y;
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    let x, y;
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const confirmSignature = async () => {
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;

    const tempContext = tempCanvas.getContext('2d');
    tempContext.fillStyle = '#FFFFFF';
    tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempContext.drawImage(canvas, 0, 0);

    const signatureImage = tempCanvas.toDataURL('image/png');

    try {
      const response = await fetch(signatureImage);
      const blob = await response.blob();

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `accountability-${station.id}-${timestamp}-${crypto.randomUUID()}.png`;

      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(fileName, blob, { contentType: 'image/png', upsert: false });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        showToast('Error uploading signature. Please try again.', 'error');
        return;
      }

      // The bucket is private: store the object path (read with createSignedUrl).
      setFormData(prev => ({ ...prev, paramedicSignature: fileName }));

      setShowSignatureCanvas(false);
      clearCanvas();
    } catch (error) {
      console.error('Error processing signature:', error);
      showToast('Error processing signature. Please try again.', 'error');
    }
  };

  useEffect(() => {
    if (showSignatureCanvas) setupCanvas();
  }, [showSignatureCanvas]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Validation
    if (!formData.formDate) {
      showToast('Date is required.', 'warning');
      return;
    }
    if (!formData.patientName.trim()) {
      showToast('Patient Name is required.', 'warning');
      return;
    }
    if (!formData.incidentNumber.trim()) {
      showToast('Incident Number is required.', 'warning');
      return;
    }
    if (!formData.medication) {
      showToast('Please select a medication.', 'warning');
      return;
    }
    if (!formData.totalAmountAdministered.trim()) {
      showToast('Total Amount Administered is required.', 'warning');
      return;
    }
    if (!formData.routeAdministered) {
      showToast('Please select a route administered.', 'warning');
      return;
    }
    if (!formData.paramedicSignature) {
      showToast('Paramedic signature is required.', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        station: station.label,
        form_date: formData.formDate,
        patient_name: formData.patientName,
        incident_number: formData.incidentNumber,
        medication: formData.medication,
        total_amount_administered: formData.totalAmountAdministered,
        route_administered: formData.routeAdministered,
        paramedic_name: `${userInfo.firstName} ${userInfo.lastName}`,
        paramedic_signature_url: formData.paramedicSignature,
        user_id: userInfo.id,
        user_email: userInfo.email,
        location: 'walton'
      };

      const { error } = await supabase
        .from('complete_usage_accountability')
        .insert([submitData]);

      if (error) throw error;

      // Reset form after successful submission
      setFormData({
        formDate: new Date().toISOString().split('T')[0],
        patientName: '',
        incidentNumber: '',
        medication: '',
        totalAmountAdministered: '',
        routeAdministered: '',
        paramedicSignature: null
      });

      showToast('Accountability form submitted successfully!', 'success');
      onNavigate('accountability-forms');
    } catch (error) {
      console.error('Error submitting form:', error);
      showToast('Error submitting form. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background-color)', padding: '20px' }}>
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '20px' }}>
        {/* Header */}
        <h1 style={{
          textAlign: 'center',
          color: 'var(--text-color)',
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '10px'
        }}>
          {station.label} Complete Usage Accountability Form
        </h1>

        <p style={{
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: '14px',
          marginBottom: '5px'
        }}>
          {station.address} (DEA#{station.dea})
        </p>

        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <strong>Note:</strong> This form is to be completed ONLY in the event that the ENTIRE VIAL of above listed medication is ADMINISTERED TO THE PATIENT.
        </div>

        {/* Form */}
        <div style={{
          background: 'var(--card-background)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '25px'
        }}>
          {/* Date */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Date *
            </label>
            <input
              type="date"
              value={formData.formDate}
              onChange={(e) => setFormData(prev => ({ ...prev, formDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            />
          </div>

          {/* Patient Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Patient Name *
            </label>
            <input
              type="text"
              value={formData.patientName}
              onChange={(e) => setFormData(prev => ({ ...prev, patientName: e.target.value }))}
              placeholder="Enter patient name"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            />
          </div>

          {/* Incident Number */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Incident Number *
            </label>
            <input
              type="text"
              value={formData.incidentNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, incidentNumber: e.target.value }))}
              placeholder="Enter incident number"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            />
          </div>

          {/* Medication */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Medication Information *
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {MEDICATIONS.map(med => (
                <label
                  key={med.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    border: formData.medication === med.id ? `2px solid ${BRANDING.primaryColor}` : '1px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.medication === med.id ? `${BRANDING.primaryColor}10` : 'white'
                  }}
                >
                  <input
                    type="radio"
                    name="medication"
                    value={med.id}
                    checked={formData.medication === med.id}
                    onChange={(e) => setFormData(prev => ({ ...prev, medication: e.target.value }))}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '16px', color: 'var(--text-color)' }}>{med.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Total Amount Administered */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Total Amount Administered *
            </label>
            <input
              type="text"
              value={formData.totalAmountAdministered}
              onChange={(e) => setFormData(prev => ({ ...prev, totalAmountAdministered: e.target.value }))}
              placeholder="Enter total amount"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            />
          </div>

          {/* Route Administered */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Route Administered *
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {ROUTES.map(route => (
                <label
                  key={route.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    border: formData.routeAdministered === route.id ? `2px solid ${BRANDING.primaryColor}` : '1px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.routeAdministered === route.id ? `${BRANDING.primaryColor}10` : 'white'
                  }}
                >
                  <input
                    type="radio"
                    name="route"
                    value={route.id}
                    checked={formData.routeAdministered === route.id}
                    onChange={(e) => setFormData(prev => ({ ...prev, routeAdministered: e.target.value }))}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>{route.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Paramedic Name (auto-filled) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Paramedic Name *
            </label>
            <input
              type="text"
              value={`${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`}
              disabled
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                background: '#f5f5f5',
                color: 'var(--text-muted)'
              }}
            />
          </div>

          {/* Paramedic Signature */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text-color)' }}>
              Paramedic Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureCanvas(true)}
              className="btn btn-primary"
              style={{ marginBottom: '10px' }}
            >
              {formData.paramedicSignature ? 'Re-Sign' : 'Sign'}
            </button>
            <div style={{
              border: '1px dashed var(--border-color)',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              minHeight: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fafafa'
            }}>
              {formData.paramedicSignature ? (
                <img
                  src={formData.paramedicSignature}
                  alt="Paramedic Signature"
                  style={{ maxWidth: '100%', maxHeight: '100px' }}
                />
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>Signature will appear here</span>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="btn btn-primary btn-full"
            style={{ padding: '15px', fontSize: '16px', fontWeight: '600' }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Form'}
          </button>

          {/* Back Button */}
          <button
            onClick={() => onNavigate('accountability-forms')}
            className="btn btn-secondary btn-full"
            style={{ marginTop: '10px', padding: '12px' }}
          >
            Back to Station Selection
          </button>
        </div>
      </div>

      {/* Signature Canvas Modal */}
      {showSignatureCanvas && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            maxWidth: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '15px', color: 'var(--text-color)' }}>Draw Your Signature</h3>
            <canvas
              ref={canvasRef}
              style={{
                border: '2px solid var(--border-color)',
                borderRadius: '8px',
                touchAction: 'none',
                cursor: 'crosshair'
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={(e) => { e.preventDefault(); startDrawing(e); }}
              onTouchMove={(e) => { e.preventDefault(); draw(e); }}
              onTouchEnd={(e) => { e.preventDefault(); stopDrawing(); }}
            />
            <div style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={clearCanvas}
                className="btn btn-secondary"
                style={{ padding: '10px 25px' }}
              >
                Clear
              </button>
              <button
                onClick={confirmSignature}
                className="btn btn-primary"
                style={{ padding: '10px 25px' }}
              >
                Confirm
              </button>
              <button
                onClick={() => { setShowSignatureCanvas(false); clearCanvas(); }}
                className="btn btn-secondary"
                style={{ padding: '10px 25px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}

export default AccountabilityForm;
